import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Pagination,
  Avatar,
  Skeleton,
  Button,
} from "@mui/material";
import { toast } from "react-toastify";
import { rental_enquiry_list, get_purchase_bank_details, get_all_bank_details } from "../../../api/ControllApi";
import TransactionDetailsModal from "../../../components/common/TransactionDetailsModal";
import { ASSET_BASE } from "../../../utils/baseUrls";

const BASE_URL = ASSET_BASE;

const RentalEnquiry = () => {
  const [enquiryList, setEnquiryList] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [rowsPerPage, setRowsPerPage] = useState(0);

  const [openModal, setOpenModal] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [allBankDetails, setAllBankDetails] = useState([]);

  /* Get list */
  const getEnquiryList = () => {
    setLoading(true);

    rental_enquiry_list()
      .then(async (res) => {
        const initialData = res?.data?.rental || [];
        setRowsPerPage(res?.data?.pagination.per_page);

        // Fetch all bank details
        let bankDetails = allBankDetails;
        if (bankDetails.length === 0) {
          try {
            const bankRes = await get_all_bank_details();
            if (bankRes.success && Array.isArray(bankRes.data)) {
              bankDetails = bankRes.data;
              setAllBankDetails(bankRes.data);
            }
          } catch (e) {
            console.error("Error fetching all bank details:", e);
          }
        }

        // Map and mark items
        const updatedData = initialData.map((enq) => {
          const propId = enq.property_id || enq.id;
          const isAdded = bankDetails.some(bd => 
            (String(bd.property_id) === String(propId)) && 
            (String(bd.type) === "rental")
          );
          return { ...enq, is_purchase_bank_details: isAdded || enq.is_purchase_bank_details };
        });

        setEnquiryList(updatedData);
      })
      .catch((err) => {
        console.error("Error fetching rental enquiries:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getEnquiryList();
  }, []);


  const trimmedSearch = search.trim().toLowerCase();

  const filteredData = enquiryList.filter((item) => {
    // If search is empty, include all items
    if (!trimmedSearch) return true;

    const propertyName = item?.property?.name || "";
    const createdAt = item?.created_at || "";

    return (
      propertyName.toLowerCase().includes(trimmedSearch) ||
      createdAt.toLowerCase().includes(trimmedSearch)
    );
  });



  /* Pagination */
  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const isVideo = (url) => {
    if (typeof url !== 'string') return false;
    return url.match(/\.(mp4|webm|mkv|mov|avi)$/i);
  };

  /* File image */
  const getFileImage = (property) => {
    // files is an ARRAY (from API)
    const files = Array.isArray(property?.files) ? property.files : [property?.files].filter(Boolean);

    if (files.length > 0) {
      // Find first non-video image
      const firstImage = files.find(file => !isVideo(file));
      if (firstImage) return firstImage;

      // If all are videos, return the first one
      return files[0];
    }

    return "/assets/images/no-image.png";
  };

  const handleViewBankDetails = async (propertyId, type) => {
    setLoading(true);
    try {
      const res = await get_purchase_bank_details(propertyId, type);
      if (res.status === true || res.success === true || res.data) {
        setPreviewData(res.data);
        setIsPreviewMode(true);
        setSelectedPropertyId(propertyId);
        setOpenModal(true);
      } else {
        toast.error(res.message || "Failed to fetch bank details");
      }
    } catch (err) {
      toast.error("Error fetching bank details");
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransaction = (propertyId) => {
    setSelectedPropertyId(propertyId);
    setOpenModal(true);
  };

  return (
    <Box>
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          Gio Rental Yield Enquiry List
        </Typography>

        <TextField
          label="Search  Gio Rental Yield Enquiry..."
          variant="outlined"
          size="small"
          sx={{ width: { xs: "100%", sm: 250 } }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </Box>

      {/* TABLE */}
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead sx={{ backgroundColor: "primary.light" }}>
            <TableRow >
              {/* <TableCell><strong>ID</strong></TableCell> */}
              <TableCell><strong>Property Name</strong></TableCell>
              <TableCell><strong>Date Of Enquiry</strong></TableCell>
              <TableCell><strong>File Image</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Tracking</strong></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading &&

              <TableRow>
                <TableCell colSpan={5} align="center">
                  Loading...
                </TableCell>
              </TableRow>
              // [...Array(rowsPerPage)].map((_, i) => (
              //   <TableRow key={i}>
              //     <TableCell><Skeleton width={40} /></TableCell>
              //     <TableCell><Skeleton width={140} /></TableCell>
              //     <TableCell><Skeleton width={100} /></TableCell>
              //     <TableCell>
              //       <Skeleton variant="rectangular" width={70} height={55} />
              //     </TableCell>
              //   </TableRow>
              // ))
            }

            {!loading && paginatedData.length > 0 &&
              paginatedData.map((enq) => {
                const property = enq?.property || {};
                const imageUrl = getFileImage(property);

                return (
                  <TableRow key={enq.id}>
                    {/* <TableCell>{enq.id}</TableCell> */}
                    <TableCell>{property?.name || "-"}</TableCell>
                    <TableCell>
                      {enq?.created_at
                        ? new Date(enq.created_at).toLocaleDateString("en-GB")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Avatar
                        src={imageUrl}
                        variant="rounded"
                        sx={{ width: 70, height: 55 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          if (enq.is_purchase_bank_details) {
                            handleViewBankDetails(enq.property_id || enq.id, "rental");
                          } else {
                            setIsPreviewMode(false);
                            setPreviewData(null);
                            setSelectedPropertyId(enq.property_id);
                            setSelectedTransactionId(enq.transaction_id);
                            setOpenModal(true);
                          }
                        }}
                        sx={{
                          textTransform: "none",
                          backgroundColor: "primary.main",
                          borderRadius: "50px",
                          fontSize: "11px",
                        }}
                      >
                        {enq.is_purchase_bank_details ? "Bank Details Added" : "Add Transaction Details"}
                      </Button>
                    </TableCell>
                    <TableCell>
                      {(!enq.dtdc_id && !enq.tracking_id) ? (
                        <Typography variant="body2" color="text.secondary">
                          Processing
                        </Typography>
                      ) : (
                        <Typography variant="body2">
                          {enq.dtdc_id || enq.tracking_id}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}

            {!loading && paginatedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No Rental Enquiry List Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && filteredData.length > rowsPerPage && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      <TransactionDetailsModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setIsPreviewMode(false);
          setPreviewData(null);
        }}
        propertyId={selectedPropertyId}
        transactionId={selectedTransactionId}
        type="rental"
        onSuccess={getEnquiryList}
        isPreview={isPreviewMode}
        data={previewData}
      />
    </Box>
  );
};

export default RentalEnquiry;
